import { useRedline } from '@libs/redline';
import { useEffect } from 'react';
import useBillet from '@common/hooks/useBillet';
import PaymentSectionSkeleton from './PaymentSectionSkeleton';
import { ClosedAgreementProps } from '@dues/pages/Partner/ClosedAgreement/types';
import CopiedCodeWarning from '@common/components/DebtsCarousel/agreementsComponents/CopiedCodeWarning';
import {
  Button,
  Conditional,
  IconCopy,
  Text,
  IconWhatsapp,
  BadgeInfo,
  Alert,
  Spinner,
  COLOR_INFO_40,
  If,
  Switch,
  Case,
  Default,
} from '@consumidor-positivo/aurora';
import isMobile from '@libs/utils/helpers/isMobile';
import { DebtData } from '@common/hooks/useDebts/types';
import { GenerateBilletResponse } from '@common/apis/billet/types';
import * as bradesco from '@dues/helpers/bradesco';

import './styles.scss';
import { shouldGetPixWithoutBillet } from '@common/hooks/usePixExperiment';

interface PaymentSectionProps {
  closedAgreement: ClosedAgreementProps;
  warningRef: React.MutableRefObject<HTMLElement | null>;
  pixCode: string;
  hasBillet: boolean;
  isPixExternal: boolean;
  billet: GenerateBilletResponse | null;
  qrCodeUrl: string;
  handleCopyPixCode: () => void;
  pixText: string;
  isBilletPollingActive: boolean;
  firstInstallmentExpDate: string;
  debtData: DebtData;
  isFirstInstallmentPaid: boolean;
  hasNewNubankDueDate: string;
  isLoadingCAPix: boolean;
}
const PaymentSection = ({
  closedAgreement,
  warningRef,
  pixCode,
  hasBillet,
  isPixExternal,
  billet,
  qrCodeUrl,
  handleCopyPixCode,
  pixText,
  isBilletPollingActive,
  firstInstallmentExpDate,
  debtData,
  hasNewNubankDueDate,
  isLoadingCAPix,
}: PaymentSectionProps) => {
  const { track: trackRedline } = useRedline();

  const { flowWithPix, singlePaymentMethod, paymentOption } = closedAgreement || {};
  const isSemParar = debtData?.partnerIdentifier === 'semparar';
  const isBradescoDesenrola = bradesco.isDesenrolaBradesco(closedAgreement?.debt);

  const shareOnWhatsApp = ({ code, typeCode }: { code: string; typeCode: string }) => {
    const message = `Aqui está o código do ${typeCode} para pagamento:\n${code}`;
    const urlWhatsApp = `https://api.whatsapp.com/send?text=${encodeURIComponent(message)}`;

    const trackPayload = {
      paymentType: typeCode === 'boleto' ? 'BILLET' : 'PIX',
      code,
      outcomeType: 'SHARE',
      agreementId: closedAgreement?.agreement?.id,
      installmentId: String(closedAgreement?.agreement?.acordoParcelas?.[0]?.id),
      channel: 'whatsapp',
    } as const;

    trackRedline.renegkit.paymentButtonClicked(trackPayload);
    trackRedline.renegkit.paymentCodeRetrieved(trackPayload);

    window.open(urlWhatsApp, '_blank');
  };

  const trackBtnEnabled = (paymentType: 'PIX' | 'BILLET', outcomeType: 'COPY' | 'SHARE' | 'DOWNLOAD', code: string) => {
    trackRedline.renegkit.paymentButtonEnabled({
      paymentType,
      outcomeType,
      code,
      agreementId: closedAgreement?.agreement?.id,
      installmentId: String(closedAgreement?.agreement?.acordoParcelas?.[0]?.id),
    });
  };

  const { handleCopyBilletCode, handleBilletDownload, buttonText } = useBillet({
    installment: closedAgreement?.agreement?.acordoParcelas?.[0],
    msUrl: closedAgreement?.debt?.partner.msUrl,
    closedAgreement,
    refCopiedWarning: warningRef,
  });

  const isOnlyPix = !!singlePaymentMethod && paymentOption?.paymentMethodName === 'PIX';
  const showPixSection = (!!flowWithPix && !singlePaymentMethod) || isOnlyPix;
  const showGeneratingPixMessage = showPixSection && isPixExternal && !pixCode && !hasBillet && !isLoadingCAPix; // TODO: devemos de fato validar pix external aqui? -> caso o rollout do checkout do rk atrasar, verificar essa validação

  const showPixCode = !!pixCode && !!flowWithPix && showPixSection;
  const showPixQRCode = !!qrCodeUrl && !isMobile();

  // Vivo Global (systemSlug 'vivo', não 'vivocyber') na variante do teste gera Pix sem boleto
  const isVivoGlobalPixTest = debtData.partnerIdentifier === 'vivo' && debtData.identifier !== 'vivocyber' && !!flowWithPix;
  const isPixWithoutBillet = shouldGetPixWithoutBillet(debtData.partnerIdentifier) || isVivoGlobalPixTest;
  const showPixWithoutBillet = !!pixCode && isPixWithoutBillet;

  const showBilletSection = hasBillet && !showPixCode;
  const showGeneratingBilletMessage = !hasBillet && !isOnlyPix && !showPixWithoutBillet && !showGeneratingPixMessage;

  const isPixBillet = !!hasBillet;
  const isLoadingPix = (showPixSection && isLoadingCAPix && isOnlyPix) || (showPixSection && !isOnlyPix && !!hasBillet && isLoadingCAPix);
  const showSkeleton = ((isBilletPollingActive && !isOnlyPix) || isLoadingPix) && !pixCode;

  const safeBillet = billet?.linhaDigitavel || '';

  useEffect(() => {
    if (showPixQRCode) {
      trackRedline.renegkit.pixQRCodeRendered({
        code: pixCode,
        url: qrCodeUrl,
        agreementId: closedAgreement?.agreement?.id,
        installmentId: String(closedAgreement?.agreement?.acordoParcelas?.[0]?.id),
      });
    }
  }, [showPixQRCode]);

  return (
    <div className="payment-section">
      <Conditional
        condition={showSkeleton}
        renderIf={<PaymentSectionSkeleton />}
        renderElse={
          <>
            <Conditional
              condition={showPixCode || showBilletSection}
              renderIf={
                <>
                  <CopiedCodeWarning ref={warningRef} />

                  <div className="payment-section__installment-data">
                    <div className="payment-section__data-container">
                      <div className="payment-section__installment-card-line">
                        <Text color="secondary">Parcela 1 de {debtData.paymentOpt.qtyInstallment}:</Text>
                        <Text color="secondary" weight="bold">
                          {debtData.paymentOpt.display.entryValue}
                        </Text>
                      </div>
                      <div className="payment-section__divider" />

                      <div className="payment-section__installment-card-line">
                        <Text color="secondary">Vencimento:</Text>
                        <Text color="secondary" weight="bold">
                          {firstInstallmentExpDate}
                        </Text>
                      </div>
                    </div>

                    <Conditional
                      condition={showPixQRCode}
                      renderIf={
                        <div className="payment-section__qrcode">
                          <img
                            className="payment-section__qrcode-img"
                            src={qrCodeUrl}
                            alt="QR Code contendo informações de pagamento do PIX"
                            width={91}
                            height={91}
                          />
                        </div>
                      }
                    />
                    <div className="payment-section__divider" />
                  </div>

                  <Conditional
                    condition={!isPixBillet}
                    renderIf={
                      <div className="payment-section__btn-container">
                        <Button expand="x" type="primary" onClick={handleCopyPixCode} onEnabled={() => trackBtnEnabled('PIX', 'COPY', pixCode)}>
                          <IconCopy />

                          {pixText}
                        </Button>
                        <Button
                          type="outlined"
                          onClick={() => shareOnWhatsApp({ code: pixCode, typeCode: 'Pix' })}
                          onEnabled={() => trackBtnEnabled('PIX', 'SHARE', pixCode)}
                        >
                          <IconWhatsapp />
                          Compartilhar
                        </Button>
                      </div>
                    }
                  />

                  <Conditional
                    condition={isPixBillet && !showBilletSection}
                    renderIf={
                      <>
                        <div className="payment-section__btn-container">
                          <Button type="primary" expand="x" onClick={handleCopyPixCode} onEnabled={() => trackBtnEnabled('PIX', 'COPY', pixCode)}>
                            <IconCopy />
                            {pixText}
                          </Button>
                          <Button
                            expand="x"
                            type="outlined"
                            onClick={() => handleCopyBilletCode(safeBillet)}
                            onEnabled={() => trackBtnEnabled('BILLET', 'COPY', safeBillet)}
                          >
                            <IconCopy />
                            <span>{buttonText}</span>
                          </Button>
                        </div>

                        <div className="payment-section__ghost-btn-container">
                          <Button type="ghost" onClick={handleBilletDownload} onEnabled={() => trackBtnEnabled('BILLET', 'DOWNLOAD', safeBillet)}>
                            Baixar boleto
                          </Button>
                          <Button
                            type="ghost"
                            onClick={() =>
                              shareOnWhatsApp({
                                code: safeBillet,
                                typeCode: 'boleto',
                              })
                            }
                            onEnabled={() => trackBtnEnabled('BILLET', 'SHARE', safeBillet)}
                          >
                            <IconWhatsapp />
                            Compartilhar
                          </Button>
                        </div>
                      </>
                    }
                  />

                  <Conditional
                    condition={showBilletSection}
                    renderIf={
                      <div className="payment-section__billet-btn-container">
                        <Button
                          expand="x"
                          type="primary"
                          onClick={() => handleCopyBilletCode(safeBillet)}
                          onEnabled={() => trackBtnEnabled('BILLET', 'COPY', safeBillet)}
                        >
                          <IconCopy />
                          <span>{buttonText}</span>
                        </Button>
                        <Button
                          expand="x"
                          type="outlined"
                          onClick={handleBilletDownload}
                          onEnabled={() => trackBtnEnabled('BILLET', 'DOWNLOAD', safeBillet)}
                        >
                          Baixar boleto
                        </Button>
                        <Button
                          expand="x"
                          type="ghost"
                          onEnabled={() => trackBtnEnabled('BILLET', 'SHARE', safeBillet)}
                          onClick={() =>
                            shareOnWhatsApp({
                              code: safeBillet,
                              typeCode: 'Boleto',
                            })
                          }
                        >
                          <IconWhatsapp />
                          Compartilhar
                        </Button>
                      </div>
                    }
                  />
                </>
              }
            />

            <Conditional
              condition={showGeneratingBilletMessage}
              renderIf={
                <>
                  <div className="payment-section__generating-billet">
                    <BadgeInfo status="info" text="Gerando dados do boleto" customIcon={<Spinner size={16} color={COLOR_INFO_40} />} />
                  </div>

                  <Conditional
                    condition={!!hasNewNubankDueDate}
                    renderIf={
                      <Text
                        variant="heading-micro"
                        weight="bold"
                        className="payment-section__pending-billet"
                        dangerouslySetInnerHTML={hasNewNubankDueDate}
                      />
                    }
                    renderElse={
                      <Text variant="heading-micro" weight="bold" className="payment-section__pending-billet">
                        Seu boleto ficará disponível <span>{isBradescoDesenrola ? 'em 7 dias úteis via e-mail.' : 'em 1 dia útil via e-mail.'}</span>
                        <br /> Agora é só aguardar!
                      </Text>
                    }
                  />
                </>
              }
            />
            <Conditional
              condition={showGeneratingPixMessage}
              renderIf={
                <>
                  <BadgeInfo status="info" text="Gerando dados do Pix" customIcon={<Spinner size={16} color={COLOR_INFO_40} />} />

                  <Text variant="heading-micro" weight="bold" className="payment-section__pending-billet">
                    Seu código Pix ficará disponível <span>em 1 dia útil via e-mail.</span>
                    <br /> Agora é só aguardar!
                  </Text>
                </>
              }
            />
          </>
        }
      />

      <If condition={!showGeneratingPixMessage && !showGeneratingBilletMessage}>
        <Alert status="info" type={2}>
          <Switch>
            <Case condition={isSemParar}>Após o pagamento do valor em aberto via PIX, sua TAG será desbloqueada em até 4h.</Case>
            <Default>
              A confirmação do pagamento pode levar <b>até 5 dias úteis, independente de ser por Boleto ou Pix</b>
            </Default>
          </Switch>
        </Alert>
      </If>
    </div>
  );
};
export default PaymentSection;
